import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import dayjs from 'dayjs';
import "bootstrap";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { Navbar } from "../Navigation/Navbar";
import {
  Typography, Button
} from "antd";
import "../../Styles/styles-StatusTracker.css";
import { useSelector, useDispatch } from "react-redux";
import { PlayerCard } from "./PlayerCard";
import { PlayerHeader } from "./PlayerHeader";
import { StatusTrackerCell } from "./StatusTrackerCell";
import { Table, Select, DatePicker, Pagination, Alert } from 'antd';
import { getStatusTrackerData, setFilter, getAvailableStatuses, getStatusTrackerFlagMetadata } from '../../store/actions/statustracker.actions'
import { formattedPlayerName } from "../../Utils/name_utils";
import {
  getPlayerGPSData,
  setPlayerFilter,
} from "../../store/actions/player.actions";

import { StatusTrackerTutorial } from "./StatusTrackerTutorial";
import { HelpButton } from "../Common/HelpButton";

const { Text } = Typography;

export const StatusTracker = ({ isEmbedded = false }) => {
  const dispatch = useDispatch();
  const {
    players,
    positions,
    playerGroups,
    info: {
      display_name: teamName,
    }
  } = useSelector(({ team }) => team);

  const {
    loading,
    trackerData,
    filters: {
      playersFilter,
      positionsFilter,
      playerGroupsFilter,
      selectedMonth,
      playerStatusesFilter
    },
    statuses
  } = useSelector(({ statusTracker }) => statusTracker);

  const headerRef = useRef(null);
  const [dateHeaders, setDateHeaders] = useState();
  const [uniqueKeys, setUniqueKeys] = useState([]);
  const [fullTrackerDS, setFullTrackerDS] = useState([]);
  const [trackerDataSource, setTrackerDataSource] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [showTutorial, setShowTutorial] = useState(false);

  const onPageChange = (page) => setPage(page);

  const prevIsEmbedded = useRef(isEmbedded);
  useEffect(() => {
    if (prevIsEmbedded.current === isEmbedded) return;
    prevIsEmbedded.current = isEmbedded;
    setPage(1);
    dispatch(setFilter({ playersFilter: [],  positionsFilter: [], playerGroupsFilter: [], playerStatusesFilter: [], selectedMonth: dayjs() }));
  }, [isEmbedded]);

  const getDatesToQuery = () => {
    let dates = []
    const startOfMonth = selectedMonth.clone().startOf('month');
    const endOfMonth = selectedMonth.clone().endOf('month');
    const minDate = dayjs.min(dayjs(), endOfMonth);

    let currDay = startOfMonth;
    while (currDay <= minDate) {
      dates.push(currDay.format('YYYY-MM-DD'));
      currDay = currDay.add(1, 'days')
      
    }
    return dates;
  }

  const handleRedirectToPlayer = useCallback((data) => async () => {
    if (data.player_id) {
      dispatch(setPlayerFilter({ player: data.player_id }));
      dispatch(getPlayerGPSData());
    }
  }, [dispatch]);

  // P5: Date columns only rebuild when the month changes — not on every pagination or filter update
  const dateColumns = useMemo(() => {
    if (!dateHeaders || Object.keys(dateHeaders).length === 0) return [];
    return Object.entries(dateHeaders).map(([dt, header]) => ({
      title: header,
      dataIndex: dt,
      key: dt,
      width: 70,
      height: 80,
      render: (uniqueKey) => {
        // Guard: trackerDataSource rows may not have this date key yet during month transition
        if (!uniqueKey) return null;
        return (
          <StatusTrackerCell
            key={isEmbedded ? `overview-${uniqueKey}` : uniqueKey}
            uniqueKey={uniqueKey}
            dateHeaders={dateHeaders}
          />
        );
      },
    }));
  }, [dateHeaders, isEmbedded]);

  // P5: Player column rebuilds on pagination/filter changes; date columns do not
  const columns = useMemo(() => {
    if (!dateHeaders || !players || Object.keys(dateHeaders).length === 0 || players.length === 0) return [];
    return [
      {
        title: <PlayerHeader uniqueKeys={uniqueKeys}/>,
        dataIndex: 'player',
        key: 'player',
        fixed: 'left',
        width: 320,
        render: (player) => (
          <Link style={{ color: "black" }} to={{ pathname: "/playerdata" }} onClick={handleRedirectToPlayer(player)}>
            <PlayerCard player={player} key={`${player.player_id}_card`} uniqueKeys={uniqueKeys}/>
          </Link>
        ),
      },
      ...dateColumns,
    ];
  }, [dateColumns, uniqueKeys, handleRedirectToPlayer, players, dateHeaders]);

  const fetchAllData = async () => {
    const dates_to_query = getDatesToQuery();
    if (!dates_to_query || dates_to_query.length === 0) return;

    // Prepare all thunk dispatches
    const allStatusPromises = dates_to_query.map(date => {
      const cellKeyObj = players.reduce((acc, player) => {
        acc[`${player.player_id}_${date}Cell`] = true;
        return acc;
      }, {});
      return dispatch(getStatusTrackerData(cellKeyObj, date));
    });

    // Execute all in parallel
    await Promise.all(allStatusPromises);

    const [year, month] = dates_to_query[0].split("-");
    dispatch(getStatusTrackerFlagMetadata(year, month));

    // Fetch available statuses (can run in parallel too, if needed)
    dispatch(getAvailableStatuses(dates_to_query[0], dates_to_query[dates_to_query.length - 1]));
  };

  useEffect(() => {
    getDatesHeaders();
    fetchAllData();
  }, [selectedMonth]);

  const formattedColumnHeader = (dt) => (
    <>
    <div style={{textAlign: 'center', padding: "10px"}}>
      <span style={{fontSize: 20}}> {dt.format('MMM')} </span>
      <span style={{fontSize: 30, lineHeight: '30px'}}> {dt.format('DD')} </span>
    </div>
    </>
  )

  const getDatesHeaders = () => {
    const daysInMonth = selectedMonth?.daysInMonth();  
  
    const dates = Array.from({ length: daysInMonth }).reduce((acc, _, i) => {
      const date = selectedMonth?.date(i + 1);
      acc[date.format("YYYY-MM-DD")] = formattedColumnHeader(date);
      return acc;
    }, {});
    
    setDateHeaders(dates);
  };

  // B1: monthUKeys passed as parameter — was previously read from an out-of-scope local variable
  const isPlayerIncluded = (player, monthUKeys) => {
    let selectPlayerGroups = [];
    if (playerGroupsFilter.length > 0) {
      selectPlayerGroups = playerGroups.filter(pg => playerGroupsFilter.includes(pg.player_group_id))
    }

    let playersWithFilteredStatuses = new Set();
    if (playerStatusesFilter.length > 0) {
      const selectPlayerStatuses = statuses.filter(s => playerStatusesFilter.includes(s.id));
      for (let uK in trackerData) {
        if (monthUKeys.has(uK)) {
          const [playerId] = uK.split("_");
          if (selectPlayerStatuses.some(ps => ps.name === trackerData[uK]?.status?.name)) {
            playersWithFilteredStatuses.add(Number(playerId));
          }
        }
      }
    }

    if (
        (playersFilter.length > 0 && !playersFilter.includes(player?.player_id)) ||
        (positionsFilter.length > 0 && !positionsFilter.includes(player?.position)) ||
        (playerGroupsFilter.length > 0 && selectPlayerGroups.length > 0 && !selectPlayerGroups.some(pg => pg.player_ids.includes(player?.player_id))) ||
        (playerStatusesFilter.length > 0 && !playersWithFilteredStatuses.has(player?.player_id))
    ) {
      return false;
    };

    return true;
  }


  // Rebuilds the full (unsliced) player dataset when month, players, or filters change.
  // Does NOT watch page — pagination never needs to redo this work.
  // B1: currMonthUKeys computed fresh here and passed into isPlayerIncluded.
  useEffect(() => {
    if (!dateHeaders || !players || Object.keys(dateHeaders).length === 0 || players.length === 0) return;

    const currMonthUKeys = new Set();
    for (const dt in dateHeaders) {
      players.forEach(p => currMonthUKeys.add(`${p.player_id}_${dt}`));
    }

    const trackerDS = [];
    const uKeys = [];

    for (let i = 0; i < players.length; i++) {
      if (!isPlayerIncluded(players[i], currMonthUKeys)) continue;

      const tableRow = { player: players[i], id: players[i]?.player_id };
      for (const dt in dateHeaders) {
        const uKey = `${players[i]?.player_id}_${dt}`;
        tableRow[String(dt)] = uKey;
        uKeys.push(uKey);
      }
      trackerDS.push(tableRow);
    }

    setUniqueKeys(uKeys);
    setTotalRecords(trackerDS.length);
    setFullTrackerDS(trackerDS);
  }, [dateHeaders, players, playersFilter, positionsFilter, playerGroupsFilter, playerStatusesFilter])

  // Slices the full dataset for the current page.
  // Runs on page change (instant — just an array slice) and whenever fullTrackerDS is rebuilt.
  useEffect(() => {
    setTrackerDataSource(fullTrackerDS.slice((page - 1) * pageSize, page * pageSize));
  }, [page, fullTrackerDS, pageSize])

  // Page is reset to 1 on every filter/month change so the table never shows an empty slice.
  // (setPage batches with the dispatch in the same render — no intermediate wrong state)
  const makeFilterHandler = (filterKey) => (value) => {
    setPage(1);
    dispatch(setFilter({ [filterKey]: value }));
  };

  const onChangePlayer          = makeFilterHandler('playersFilter');
  const onChangePosition        = makeFilterHandler('positionsFilter');
  const setSelectedPlayerGroups = makeFilterHandler('playerGroupsFilter');
  const setSelectedPlayerStatuses = makeFilterHandler('playerStatusesFilter');

  const onMonthChange = (date) => {
    setPage(1);
    dispatch(setFilter({ selectedMonth: date }));
  };

  return (
    <>
      {
        !isEmbedded && (
          <>
            <Navbar />
            <Alert
              style={{width: '98%', margin: '10px', justifySelf: 'left'}}
              message={
              <span style={{float:'left', fontSize: '16px'}}><b>
                For a quick tour, <Typography.Link type="text" style={{marginLeft: '3px'}} onClick={() => setShowTutorial(true)}>Click here!</Typography.Link>
                </b>   
              </span>}
              type="info"
              closable
              showIcon
            />

            <HelpButton 
              page="Status Tracker" 
              extraContent={
              <Typography.Title level={5}>For a quick tour, 
                <Typography.Link type="text" style={{marginLeft: '3px'}} onClick={() => setShowTutorial(true)}>Click here!</Typography.Link>
              </Typography.Title>}
            />

            <StatusTrackerTutorial
              setOpen={setShowTutorial}
              isOpen={showTutorial}
            />
          </>
        )
      }
      
     
      <div className="tracker-container">
        <div className="tracker-header" ref={headerRef}>
          {!isEmbedded && (
            <div className="tracker-filter-container">
              <div>
                <DatePicker
                  value={selectedMonth}
                  style={{ marginLeft: "10px" }}
                  onChange={onMonthChange}
                  picker="month"
                  allowClear={false}
                  format={"MMM YYYY"}
                />
              </div>
              <div>
                <Select
                  allowClear
                  showSearch
                  style={{ width: 320, marginLeft: "10px" }}
                  placeholder="Choose Players"
                  value={playersFilter}
                  onChange={onChangePlayer}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  mode="multiple"
                  maxTagCount={1}
                >
                  {players !== null ? (
                    players.map((player, i) => (
                      <Option key={i} value={player.player_id}>
                        {`${formattedPlayerName(
                          teamName,
                          player.first_name + " " + player.last_name
                        )} #${player.number}`}
                      </Option>
                    ))
                  ) : (
                    <></>
                  )}
                </Select>
              </div>
              <div>
                <Select
                  allowClear
                  showSearch
                  style={{ width: 260, marginLeft: "10px" }}
                  placeholder="Choose Positions"
                  value={positionsFilter}
                  onChange={onChangePosition}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  mode="multiple"
                  maxTagCount={3}
                >
                  {positions !== null ? (
                    positions.map((pos, i) => (
                      <Option key={i} value={pos}>{pos}</Option>
                    ))
                  ) : (
                    <></>
                  )}
                </Select>
              </div>
              <div>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="value"
                  placeholder="Choose Player Groups"
                  value={playerGroupsFilter}
                  style={{ width: 250, marginLeft: "10px" }}
                  onChange={setSelectedPlayerGroups}
                  mode="multiple"
                  maxTagCount={1}
                >
                  {playerGroups.map((playerGroup, i) => (
                    <Option key={i} value={playerGroup?.player_group_id}>
                      {playerGroup?.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="value"
                  placeholder="Choose Player Statuses"
                  value={playerStatusesFilter}
                  style={{ width: 305, marginLeft: "10px" }}
                  onChange={setSelectedPlayerStatuses}
                  mode="multiple"
                  maxTagCount={2}
                >
                  {statuses.map((status, i) => (
                    <Option key={i} value={status?.id}>{status?.name}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        </div>

        <div className="tracker-legend-pagination-row">
          <div className="tracker-status-legend">
            {isEmbedded && (
              <div>
                <DatePicker
                  value={selectedMonth}
                  style={{ marginLeft: "10px" }}
                  onChange={onMonthChange}
                  picker="month"
                  allowClear={false}
                  format={"MMM YYYY"}
                />
              </div>
            )}
            {statuses.map((status, i) => (
              <div
                className="tracker-status-legend-container"
                key={`tracker-status-legend-${i}`}
              >
                <div style={{ width: 20, height: 20, backgroundColor: status.color }}></div>
                <div style={{ marginLeft: 10 }}>{status.name}</div>
              </div>
            ))}
          </div>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={Math.max(totalRecords, 1)}
            onChange={onPageChange}
            hideOnSinglePage={false}
            showSizeChanger={false}
          />
        </div>

        <div className="tracker-table-container">
          <Table
            className="status-tracker-table"
            columns={columns}
            dataSource={trackerDataSource}
            bordered
            pagination={false}
            scroll={{ y: 550 }}
            sticky
            rowKey="id"
          />
        </div>
      </div>
    </>
  
  );
};
